import React, { useState } from 'react';
import { toast } from 'sonner';
import { invoicesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Receipt, Eye, Download, Trash2, FilePlus2, Archive } from 'lucide-react';
import { FinalInvoiceDialog } from './FinalInvoiceDialog';

export const InvoicesTab = ({
  job,
  quotes,
  invoices,
  jobId,
  statusConfig,
  formatDate,
  onDownloadPdf,
  onChanged,
}) => {
  const [finalOpen, setFinalOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone. Tip: choose "Archive" instead if you want to keep the audit trail.`)) return;
    try {
      await invoicesAPI.delete(inv.id);
      toast.success('Invoice deleted');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleArchive = async (inv) => {
    if (inv.status === 'cancelled') {
      toast.info('Invoice is already archived');
      return;
    }
    if (!window.confirm(`Archive invoice ${inv.invoice_number}? It will stop counting toward totals and the burndown chart, but stays in the audit trail.`)) return;
    try {
      await invoicesAPI.archive(inv.id);
      toast.success('Invoice archived');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive invoice');
    }
  };

  const handleToggleGst = async (inv, nextChargeGst) => {
    try {
      const subtotal = parseFloat(inv.subtotal || 0);
      const tax = nextChargeGst ? Math.round(subtotal * 0.1 * 100) / 100 : 0;
      const total = Math.round((subtotal + tax) * 100) / 100;
      await invoicesAPI.update(inv.id, { charge_gst: nextChargeGst, tax, total, amount_due: total });
      toast.success(`GST ${nextChargeGst ? 'enabled' : 'disabled'}`);
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Invoices</CardTitle>
          <Button size="sm" onClick={() => setFinalOpen(true)} data-testid="generate-final-invoice-btn">
            <FilePlus2 className="h-4 w-4 mr-1" /> Generate Invoice
          </Button>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-xs mt-2">Click "Generate Invoice" to issue a progress claim or final invoice.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((inv) => (
                <div key={inv.id} className="border border-border rounded-lg p-4 hover:bg-secondary/20" data-testid={`invoice-card-${inv.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 flex-wrap">
                        {inv.invoice_number}
                        {inv.progress_invoice_index ? (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">#{inv.progress_invoice_index}</span>
                        ) : null}
                        {inv.invoice_kind === 'progress' ? (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded" data-testid={`progress-badge-${inv.id}`}>
                            Progress{inv.progress_pct ? ` ${Number(inv.progress_pct)}%` : ''}
                          </span>
                        ) : inv.invoice_kind === 'final' ? (
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">Final</span>
                        ) : null}
                        <span className={`text-xs px-2 py-0.5 rounded ${inv.charge_gst === false ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                          {inv.charge_gst === false ? 'GST OFF' : 'GST ON'}
                        </span>
                      </h4>
                      <p className="text-sm text-muted-foreground">Due: {formatDate(inv.due_date)}</p>
                      <p className="text-lg font-bold mt-1">${inv.total?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Subtotal ${inv.subtotal?.toFixed(2)} · GST ${inv.tax?.toFixed(2)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${statusConfig[inv.status]?.color}`}>{statusConfig[inv.status]?.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 items-center">
                    <Button size="sm" variant="outline" onClick={() => { setSelected(inv); setViewOpen(true); }} data-testid={`view-invoice-${inv.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDownloadPdf?.(inv.id, 'invoice')}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <div className="flex items-center gap-2 ml-2 px-2 py-1 rounded bg-secondary/40 border border-border">
                      <Label htmlFor={`gst-${inv.id}`} className="text-xs cursor-pointer">Apply GST</Label>
                      <Switch
                        id={`gst-${inv.id}`}
                        checked={inv.charge_gst !== false}
                        onCheckedChange={(v) => handleToggleGst(inv, v)}
                        data-testid={`invoice-gst-toggle-${inv.id}`}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-yellow-400 ml-auto"
                      onClick={() => handleArchive(inv)}
                      data-testid={`archive-invoice-${inv.id}`}
                      disabled={inv.status === 'cancelled'}
                    >
                      <Archive className="h-4 w-4 mr-1" /> Archive
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(inv)}
                      data-testid={`delete-invoice-${inv.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Invoice */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.invoice_number}</DialogTitle>
            <DialogDescription>Invoice line items and totals.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Status: <span className={`px-2 py-0.5 rounded ${statusConfig[selected.status]?.color}`}>{statusConfig[selected.status]?.label}</span>
                </span>
                <span className="text-muted-foreground">Due: {formatDate(selected.due_date)}</span>
              </div>
              <div className="border rounded-lg p-4 space-y-2">
                {selected.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.description}</span>
                    <span>${(parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-1">
                <p className="font-bold">Total: ${selected.total?.toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final Invoice modal (with built-in over-PO warning) */}
      <FinalInvoiceDialog
        open={finalOpen}
        onOpenChange={setFinalOpen}
        job={job}
        quotes={quotes}
        jobId={jobId}
        onSuccess={onChanged}
      />
    </>
  );
};

export default InvoicesTab;
